import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Users, Mic, Loader2, Check, Wand2, UserPlus, Trash2,
  ChevronDown, ChevronRight
} from "lucide-react";
import { API, BACKEND_URL } from "../lib/constants";

const VOICE_OPTIONS = [
  { value: "alloy", label: "Alloy", gender: "neutral" },
  { value: "ash", label: "Ash", gender: "male" },
  { value: "coral", label: "Coral", gender: "female" },
  { value: "echo", label: "Echo", gender: "male" },
  { value: "fable", label: "Fable", gender: "male" },
  { value: "nova", label: "Nova", gender: "female" },
  { value: "onyx", label: "Onyx", gender: "male" },
  { value: "sage", label: "Sage", gender: "female" },
  { value: "shimmer", label: "Shimmer", gender: "female" },
];

export default function CastRoster({ seriesOverride }) {
  const series = seriesOverride || "the-notarys-shadow";
  const [roster, setRoster] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoPopulating, setAutoPopulating] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [assignJobId, setAssignJobId] = useState(null);
  const [assignProgress, setAssignProgress] = useState({ done: 0, total: 0, current: "" });
  const [assignDone, setAssignDone] = useState(false);
  const [expandedMember, setExpandedMember] = useState(null);
  const [allChars, setAllChars] = useState([]);
  const [assignSeason, setAssignSeason] = useState(null);

  const fetchRoster = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/cast/roster/${series}`);
      setRoster(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [series]);

  useEffect(() => { fetchRoster(); }, [fetchRoster]);

  useEffect(() => {
    axios.get(`${API}/characters?series=${series}`)
      .then(res => setAllChars(Array.isArray(res.data) ? res.data : []))
      .catch(console.error);
  }, [series]);

  const imgUrl = (src) => src ? (src.startsWith("http") ? src : `${BACKEND_URL}${src}`) : null;

  const autoPopulate = async () => {
    setAutoPopulating(true);
    try {
      await axios.post(`${API}/cast/auto-populate/${series}`);
      await fetchRoster();
    } catch (e) {
      alert("Failed: " + (e.response?.data?.detail || e.message));
    } finally { setAutoPopulating(false); }
  };

  const saveRoster = async () => {
    if (!roster) return;
    setSaving(true);
    try {
      await axios.post(`${API}/cast/roster`, {
        series,
        members: roster.members.map(m => ({
          character_id: m.character_id,
          role: m.role,
          voice_preset: m.voice_preset || m.character?.ai_voice || "alloy",
          pitch_shift: m.pitch_shift || 0,
          notes: m.notes || "",
        }))
      });
    } catch (e) {
      alert("Save failed");
    } finally { setSaving(false); }
  };

  const removeMember = (charId) => {
    setRoster(prev => ({
      ...prev,
      members: prev.members.filter(m => m.character_id !== charId)
    }));
  };

  const updateMember = (charId, field, value) => {
    setRoster(prev => ({
      ...prev,
      members: prev.members.map(m =>
        m.character_id === charId ? { ...m, [field]: value } : m
      )
    }));
  };

  const autoAssign = async () => {
    setAssigning(true);
    setAssignDone(false);
    setAssignProgress({ done: 0, total: 0, current: "" });
    try {
      const res = await axios.post(`${API}/cast/auto-assign/${series}`, {
        season: assignSeason
      });
      const jobId = res.data.job_id;
      setAssignJobId(jobId);
      setAssignProgress({ done: 0, total: res.data.total, current: "" });

      const poll = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API}/cast/assign-status/${jobId}`);
          const job = statusRes.data;
          setAssignProgress({ done: job.done, total: job.total, current: job.current || "" });

          if (job.status === "done") {
            clearInterval(poll);
            setAssigning(false);
            setAssignDone(true);
          } else if (job.status === "error") {
            clearInterval(poll);
            setAssigning(false);
            alert("Assignment failed: " + (job.error || "Unknown"));
          }
        } catch (pollErr) { console.error(pollErr); }
      }, 4000);
    } catch (e) {
      setAssigning(false);
      alert("Failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const hasMembers = roster && roster.members && roster.members.length > 0;
  const narrator = hasMembers ? roster.members.find(m => m.role === "narrator") : null;

  return (
    <div data-testid="cast-roster">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-cinzel text-xl font-bold text-white mb-1">Series Cast Roster</h2>
          <p className="text-gray-500 text-sm">Define characters + voices once. AI auto-assigns them per episode.</p>
        </div>
        <div className="flex gap-2">
          {hasMembers && (
            <button onClick={saveRoster} disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-bold rounded-lg transition-colors"
              data-testid="save-roster-btn">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save Roster
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-600" /></div>
      ) : !hasMembers ? (
        <div className="text-center py-16 border border-dashed border-white/10 rounded-xl">
          <Users size={40} className="mx-auto text-gray-600 mb-4" />
          <h3 className="text-white font-bold mb-2">No Cast Roster Yet</h3>
          <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
            Auto-populate from your character database. This creates a Narrator and adds all series characters with their voice settings.
          </p>
          <button onClick={autoPopulate} disabled={autoPopulating}
            className="flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg mx-auto transition-colors"
            data-testid="auto-populate-btn">
            {autoPopulating ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />}
            {autoPopulating ? "Creating roster..." : "Auto-Populate Cast Roster"}
          </button>
        </div>
      ) : (
        <>
          {/* Narrator Badge */}
          {narrator && (
            <div className="mb-6 rounded-xl bg-gradient-to-r from-amber-500/10 to-red-500/10 border border-amber-500/20 overflow-hidden"
              data-testid="narrator-badge">
              <div className="flex items-center gap-3 p-4 cursor-pointer"
                onClick={() => setExpandedMember(expandedMember === "narrator" ? null : "narrator")}>
                <Mic size={20} className="text-amber-400" />
                <div className="flex-1">
                  <span className="text-amber-400 text-sm font-bold">NARRATOR</span>
                  <span className="text-white ml-2">{narrator.character?.name || "Unknown"}</span>
                  <span className="text-gray-500 text-xs ml-2">Included in every episode</span>
                </div>
                <span className="text-gray-500 text-xs">Voice: {narrator.voice_preset || narrator.character?.ai_voice || "nova"}</span>
                {expandedMember === "narrator" ? <ChevronDown size={14} className="text-gray-500" /> : <ChevronRight size={14} className="text-gray-500" />}
              </div>
              {expandedMember === "narrator" && (
                <div className="px-4 pb-4 pt-1 border-t border-amber-500/10">
                  <div className="flex items-center gap-3 text-xs text-gray-400">
                    <span>Voice: <strong className="text-white">{narrator.voice_preset || narrator.character?.ai_voice || "nova"}</strong></span>
                    <span>Speed: <strong className="text-white">{(narrator.speed || 1.0).toFixed(1)}x</strong></span>
                    <span>Pitch: <strong className="text-white">{narrator.pitch_shift || 0}st</strong></span>
                  </div>
                  <p className="text-[10px] text-amber-400/70 mt-2">Edit voice settings in Assets → Voice Lab</p>
                </div>
              )}
            </div>
          )}

          {/* Cast Members */}
          <div className="space-y-2 mb-8" data-testid="cast-members">
            {roster.members.filter(m => m.role !== "narrator").map(member => {
              const char = member.character;
              const isExpanded = expandedMember === member.character_id;
              return (
                <div key={member.character_id}
                  className="rounded-lg bg-white/[0.02] border border-white/8 hover:border-white/15 transition-all overflow-hidden"
                  data-testid={`cast-member-${member.character_id}`}>
                  <div className="flex items-center gap-3 p-3 cursor-pointer"
                    onClick={() => setExpandedMember(isExpanded ? null : member.character_id)}>
                    {/* Portrait */}
                    {char?.preview_image_url ? (
                      <img src={imgUrl(char.preview_image_url)} alt={char?.name}
                        className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-gray-500 text-xs font-bold">
                        {char?.name?.charAt(0) || "?"}
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      <h4 className="text-white text-sm font-bold truncate">{char?.name || "Unknown"}</h4>
                      <p className="text-gray-500 text-[10px] truncate">{char?.description || ""}</p>
                    </div>

                    {/* Role badge */}
                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded ${
                      member.role === "character" ? "bg-blue-500/20 text-blue-400" : "bg-purple-500/20 text-purple-400"
                    }`}>
                      {member.role}
                    </span>

                    {/* Voice */}
                    <span className="text-gray-500 text-xs w-16 text-right">{member.voice_preset || char?.ai_voice || "alloy"}</span>

                    {isExpanded ? <ChevronDown size={14} className="text-gray-500" /> : <ChevronRight size={14} className="text-gray-500" />}
                  </div>

                  {/* Read-only voice info — edit in Voice Lab */}
                  {isExpanded && (
                    <div className="px-4 pb-4 pt-1 border-t border-white/5 space-y-2">
                      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-400">
                        <span>Role: <strong className="text-white">{member.role}</strong></span>
                        <span>Voice: <strong className="text-white">{member.voice_preset || char?.ai_voice || "alloy"}</strong></span>
                        <span>Speed: <strong className="text-white">{(member.speed || 1.0).toFixed(1)}x</strong></span>
                        <span>Pitch: <strong className="text-white">{member.pitch_shift || 0}st</strong></span>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-[10px] text-amber-400/70">Edit voice settings in Assets → Voice Lab</p>
                        <button onClick={() => removeMember(member.character_id)}
                          className="flex items-center gap-1 px-3 py-1.5 text-red-400 hover:bg-red-500/10 text-[10px] font-bold rounded transition-colors">
                          <Trash2 size={10} /> Remove from cast
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Auto-Assign Section */}
          <div className="p-5 rounded-xl bg-white/[0.02] border border-white/10" data-testid="auto-assign-section">
            <div className="flex items-center gap-2 mb-3">
              <Wand2 size={16} className="text-red-500" />
              <h3 className="font-cinzel font-bold text-white text-sm">Auto-Assign Cast to Episodes</h3>
            </div>
            <p className="text-gray-500 text-xs mb-4">
              AI reads each episode's summary and automatically assigns which cast members appear, with estimated line counts per character.
            </p>

            <div className="flex items-center gap-3 mb-4">
              <select value={assignSeason || ""}
                onChange={e => setAssignSeason(e.target.value ? parseInt(e.target.value) : null)}
                className="px-3 py-2 bg-[#0a0a0a] border border-white/10 rounded-lg text-sm text-white outline-none">
                <option value="">All Seasons</option>
                {[1,2,3,4,5,6,7,8,9,10].map(s => <option key={s} value={s}>Season {s}</option>)}
              </select>

              <button onClick={autoAssign} disabled={assigning}
                className="flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-700 disabled:bg-red-800 disabled:opacity-50 text-white text-sm font-bold rounded-lg transition-colors"
                data-testid="auto-assign-btn">
                {assigning ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
                {assigning ? "Assigning..." : "Auto-Assign Characters"}
              </button>
              {!assigning && <span className="text-gray-600 text-xs">Runs in background</span>}
            </div>

            {/* Assignment progress */}
            {assigning && (
              <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg mb-3">
                <div className="flex items-center gap-2 mb-2">
                  <Loader2 size={14} className="text-amber-400 animate-spin" />
                  <span className="text-amber-400 text-xs font-bold">
                    Assigning cast... {assignProgress.done}/{assignProgress.total}
                  </span>
                  <span className="text-gray-500 text-xs ml-auto">Navigate away freely</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-amber-500 rounded-full transition-all duration-700"
                      style={{ width: `${assignProgress.total > 0 ? (assignProgress.done / assignProgress.total) * 100 : 0}%` }} />
                  </div>
                  <span className="text-amber-400 text-[10px] font-mono">
                    {assignProgress.total > 0 ? Math.round((assignProgress.done / assignProgress.total) * 100) : 0}%
                  </span>
                </div>
                {assignProgress.current && <p className="text-gray-500 text-[10px] mt-1">Processing: {assignProgress.current}</p>}
              </div>
            )}

            {assignDone && (
              <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                <div className="flex items-center gap-2">
                  <Check size={14} className="text-green-400" />
                  <span className="text-green-400 text-xs font-bold">
                    Cast assigned to {assignProgress.total} episodes with estimated line counts
                  </span>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
